import { parsePhoneNumberFromString } from "libphonenumber-js";
import { memo } from "react";

import { InputPhoneNumber } from "@/components/Input/Phone/InputPhoneNumber";
import type { PhoneValue } from "@/components/Input/Phone/state.machine";
import { useAuthStore } from "@/lib/auth";

import type { FieldRenderInputProps } from "../../types";

import type { BaseFieldProps } from "./BaseField";
import { BaseField } from "./BaseField";

/**
 * 電話番号フィールドのプロパティ
 */
type PhoneFieldProps = Omit<BaseFieldProps, "renderInput"> & {
  dialCode?: string;
};

// @note Business Operation Engine のFormはE.164形式の電話番号を要求するため、E.164形式に変換して返す
const formatToInternational = (value: PhoneValue): string | undefined => {
  const rawNumber = value.phoneNumber.replace(/-/g, ""); // ハイフンを除去
  const phoneNumber = parsePhoneNumberFromString(rawNumber, {
    defaultCallingCode: value.dialCode,
  });

  if (phoneNumber) {
    return phoneNumber.format("E.164"); // "+818083773845" のようなフォーマット
  }

  return undefined; // 無効な番号の場合
};

// E.164形式の電話番号（例: "+818012345678"）をPhoneValue形式に変換する
const formatFromInternational = (
  e164Value: string,
  locationDialCode: string,
): PhoneValue => {
  const phoneNumber = parsePhoneNumberFromString(e164Value);

  if (phoneNumber) {
    return {
      phoneNumber: phoneNumber.formatNational(), // ハイフンありの国内形式（例: "080-1234-5678"）
      dialCode: phoneNumber.countryCallingCode,
    };
  }

  // 変換できない場合は空のPhoneValueを返す
  return {
    phoneNumber: "",
    dialCode: locationDialCode,
  };
};

/**
 * 電話番号フィールドコンポーネント
 * 電話番号の入力を提供します
 */
export const PhoneField = memo(
  ({
    fieldId,
    field,
    readOnly,
    className,
    dialCode,
    layout,
    "data-testid": testId,
  }: PhoneFieldProps) => {
    const locationDialCode =
      useAuthStore.getState().getLocationDialCode() ?? "";
    return (
      <BaseField
        fieldId={fieldId}
        field={field}
        readOnly={readOnly}
        className={className}
        data-testid={testId}
        layout={layout}
        renderInput={({ value, onChange, onBlur }: FieldRenderInputProps) => (
          <InputPhoneNumber
            value={
              typeof value === "string" && value.startsWith("+")
                ? formatFromInternational(value, locationDialCode)
                : {
                    phoneNumber: typeof value === "string" ? value : "",
                    dialCode: dialCode ?? locationDialCode ?? "",
                  }
            }
            onChange={(val) => {
              onChange(formatToInternational(val));
            }}
            onBlur={onBlur}
            width="100%"
            data-testid={`${testId || (field ? `field-${field.id}` : `field-${fieldId}`)}-input`}
            data-role="form-field-input"
            readOnly={readOnly}
            required={field.required}
          />
        )}
      />
    );
  },
);

PhoneField.displayName = "PhoneField";
